import moment, { type Moment } from "moment"
import { startTracking, stopTracking } from "@/mobile_app/services/tracking-service"
import { useBackgroundLocation } from "@/mobile_app/services/background-location-service"
import { ModelFactory } from "@/factories/model-factory.ts"
import { WorkPeriod } from "@/models/work-period.model.ts"
import { modifyTime } from "@/helper/time-helper.ts"
import type { AppWorkPeriodController } from "@/controllers/model-controllers/app-work-period-controller.ts"
import type { WorkPeriodTemplateController } from "@/controllers/model-controllers/work-period-template-controller.ts"
import type { AxiosResponse } from "axios"
import { generateWorkPeriodValidationError } from "@/helper/server_validation_exceptions/work-period-validation-exception-helper.ts"
import { WorkPeriodsApi, type WorkPeriod as SDKWorkPeriodType } from "@sdk/api"
import { getApiConfiguration } from "@/services/api-config"
import { useErrorHandler } from "@/services/error-handler"
import { buildODataQueryParams } from "@/dashboard/utils/odata-query-builder"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import type { WorkPeriodTemplate } from "@/models/work-period-template.model.ts"

export function useWorkPeriodService() {
  const { trackLocation } = useBackgroundLocation()
  const workPeriodsApi = new WorkPeriodsApi(getApiConfiguration())
  const { handleError } = useErrorHandler()

  /**
   * Builds the shared 400 status-code handler that turns a validation response into a
   * WorkPeriod validation error and forwards it to the optional error callback.
   */
  function createValidationStatusCodeHandlers(onError?: (error: unknown) => void) {
    return [
      {
        statusCode: 400,
        callback: (response: AxiosResponse) => {
          const error = generateWorkPeriodValidationError(response.data)
          if (onError) {
            onError(error)
          }
          return true
        }
      }
    ]
  }

  /**
   * Starts background tracking and location services for an active (open-ended) work period.
   */
  async function startShiftTracking(workPeriod: WorkPeriod) {
    await startTracking("Actieve dienst", workPeriod.timeStart.valueOf())
    trackLocation()
  }

  /**
   * Starts a work period with tracking and location services
   * @param workPeriodController The controller to create the work period
   * @param knownBusinessEntityContact The known business entity contact object
   * @param selectedDate The selected date for the work period
   * @param existingWorkPeriods Optional array of existing work periods to check for conflicts
   * @param onError Optional error handler callback
   * @returns The created work period or undefined if creation failed
   */
  async function startWorkPeriod(
    workPeriodController: AppWorkPeriodController,
    knownBusinessEntityContact: KnownBusinessEntityContact,
    selectedDate: Moment,
    existingWorkPeriods: WorkPeriod[] = [],
    onError?: (error: unknown) => void
  ): Promise<WorkPeriod | undefined> {
    // Default moment will be current time
    let defaultTimeStart = moment()
      .set({
        year: selectedDate.year(),
        month: selectedDate.month(),
        date: selectedDate.date()
      })
      .startOf("minute") // Minute granularity

    // Check if default moment is during a work period. If so, set now to the end of that work period.
    // Otherwise, when the user creates a new work period during a time that is already covered by
    // a work period, an error will be thrown.
    const existingWorkPeriod = existingWorkPeriods.find((workPeriod) => {
      return (
        workPeriod.timeStart.isSameOrBefore(defaultTimeStart) &&
        workPeriod.timeEnd!.isAfter(defaultTimeStart)
      )
    })
    if (existingWorkPeriod) {
      defaultTimeStart = existingWorkPeriod.timeEnd!.clone()
    }

    const workPeriod = ModelFactory.createModel(
      WorkPeriod,
      {
        owner: knownBusinessEntityContact,
        activities: [],
        timeStart: modifyTime(selectedDate, defaultTimeStart)
      },
      false
    )

    const statusCodeHandlers = createValidationStatusCodeHandlers(onError)

    try {
      const createdWorkPeriod = await workPeriodController.create(workPeriod, {
        statusCodeHandlers
      })

      // Start tracking and location services with the work period start time
      if (createdWorkPeriod) {
        await startShiftTracking(createdWorkPeriod)
      }

      return createdWorkPeriod
    } catch (error) {
      if (onError) {
        onError(error)
      }
      return undefined
    }
  }

  /**
   * Creates a work period from a saved work-day template for the selected date. The projection of
   * the template's time-only values onto the date and the creation of the work period together
   * with its activities happen atomically on the server, so no partial state can be left behind.
   * The resulting work period is registered in the app store. Kept separate from startWorkPeriod
   * so the manual "start dienst" flow stays untouched.
   * @param workPeriodController The controller used to register the created work period in the store
   * @param workPeriodTemplateController The controller that maps to the atomic template endpoint
   * @param template The work period template to apply
   * @param selectedDate The date to project the template's times onto
   * @param onError Optional error handler callback
   * @returns The created work period or undefined if creation failed
   */
  async function startWorkPeriodFromTemplate(
    workPeriodController: AppWorkPeriodController,
    workPeriodTemplateController: WorkPeriodTemplateController,
    template: WorkPeriodTemplate,
    selectedDate: Moment,
    onError?: (error: unknown) => void
  ): Promise<WorkPeriod | undefined> {
    const statusCodeHandlers = createValidationStatusCodeHandlers(onError)

    try {
      const createdWorkPeriod = await workPeriodTemplateController.createWorkPeriod(
        template.id!,
        selectedDate,
        { statusCodeHandlers }
      )

      if (!createdWorkPeriod) {
        return undefined
      }

      const storedWorkPeriod = await workPeriodController.addExistingWorkPeriod(createdWorkPeriod)

      // Only an open-ended work period represents an active shift that needs tracking.
      if (!storedWorkPeriod.timeEnd) {
        await startShiftTracking(storedWorkPeriod)
      }

      return storedWorkPeriod
    } catch (error) {
      if (onError) {
        onError(error)
      }
      return undefined
    }
  }

  /**
   * Stops tracking and location services when a work period ends
   */
  async function stopWorkPeriodTracking() {
    await stopTracking()
  }

  /**
   * Checks for active work periods and resumes tracking if found
   * @param knownBusinessEntityContact The known business entity contact object
   * @returns The active work period if found, undefined otherwise
   */
  async function checkAndResumeActiveWorkPeriod(
    knownBusinessEntityContact: KnownBusinessEntityContact
  ): Promise<WorkPeriod | undefined> {
    // Build OData filter: owner eq 'id' and time_end eq null
    const filterQuery = `owner eq '${knownBusinessEntityContact.id}' and time_end eq null`

    try {
      const response = await workPeriodsApi.workPeriodsList(
        ...buildODataQueryParams({
          $filter: filterQuery,
          $orderby: "time_start desc",
          getNested: true,
          normalize: "false"
        })
      )

      // Handle response structures
      let workPeriods: SDKWorkPeriodType[] = []
      const data = response.data as any

      if (Array.isArray(data)) {
        workPeriods = data as SDKWorkPeriodType[]
      } else if (data && Array.isArray(data.results)) {
        workPeriods = data.results as SDKWorkPeriodType[]
      } else if (data && data.data && Array.isArray(data.data)) {
        workPeriods = data.data as SDKWorkPeriodType[]
      }

      // Convert SDK WorkPeriod interface to model WorkPeriod class and get the first active one
      const activeWorkPeriods = workPeriods.map((wp) =>
        ModelFactory.createModel(WorkPeriod, wp as any, false)
      )
      const activeWorkPeriod = activeWorkPeriods[0]

      if (activeWorkPeriod) {
        // Start tracking with the work period start time
        await startShiftTracking(activeWorkPeriod)
      } else {
        console.log("No active work period found, stopping tracking")
        await stopTracking()
      }

      return activeWorkPeriod
    } catch (error) {
      handleError(error, "Actieve werkperiode ophalen")
      await stopTracking()
      return undefined
    }
  }

  return {
    startWorkPeriod,
    startWorkPeriodFromTemplate,
    stopWorkPeriodTracking,
    checkAndResumeActiveWorkPeriod
  }
}
